/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';
import debounce from 'lodash/debounce';
import type { Map } from 'mapbox-gl';

import type { SearchResultItem } from '../../../api/search.types';

interface UseVisibleMapItemsParams {
    mapInstance: Map | null;
    data?: Array<SearchResultItem & { latitude: string; longitude: string }>;
    debounceMs?: number;
}

interface UseVisibleMapItemsResult {
    /** null = not yet computed or no map/data (treat as "all visible"); [] = computed and none visible */
    visibleItemIds: string[] | null;
}

/**
 * Represents the actual visible bounds calculated from viewport pixel corners.
 * This is more accurate than mapInstance.getBounds() at high zoom levels
 * due to map projection distortion.
 */
interface ViewportBounds {
    north: number;
    south: number;
    east: number;
    west: number;
}

/**
 * Gets the actual visible bounds by unproject-ing the pixel corners of the map canvas.
 * This method is more accurate than getBounds() because it accounts for
 * Web Mercator projection distortion at the viewport edges, which becomes
 * more noticeable at high zoom levels.
 */
function getAccurateViewportBounds(mapInstance: Map): ViewportBounds | null {
    const canvas = mapInstance.getCanvas();
    if (!canvas) return null;

    const width = canvas.clientWidth;
    const height = canvas.clientHeight;

    // Unproject the four corners of the viewport
    const topLeft = mapInstance.unproject([0, 0]);
    const topRight = mapInstance.unproject([width, 0]);
    const bottomLeft = mapInstance.unproject([0, height]);
    const bottomRight = mapInstance.unproject([width, height]);

    // Calculate the actual bounding box from all corners
    // This accounts for any rotation or projection distortion
    return {
        north: Math.max(topLeft.lat, topRight.lat, bottomLeft.lat, bottomRight.lat),
        south: Math.min(topLeft.lat, topRight.lat, bottomLeft.lat, bottomRight.lat),
        east: Math.max(topLeft.lng, topRight.lng, bottomLeft.lng, bottomRight.lng),
        west: Math.min(topLeft.lng, topRight.lng, bottomLeft.lng, bottomRight.lng),
    };
}

/**
 * Checks if a coordinate is within the given viewport bounds
 */
function isPointInBounds(lat: number, lng: number, bounds: ViewportBounds): boolean {
    return (
        lat >= bounds.south &&
        lat <= bounds.north &&
        lng >= bounds.west &&
        lng <= bounds.east
    );
}

/**
 * Filters data items to only those visible within the current viewport bounds
 */
function getVisibleItems(
    data: Array<SearchResultItem & { latitude: string; longitude: string }>,
    bounds: ViewportBounds
): string[] {
    const visibleIds: string[] = [];

    data.forEach((item) => {
        const lat = parseFloat(item.latitude);
        const lng = parseFloat(item.longitude);

        if (
            !Number.isNaN(lat) &&
            !Number.isNaN(lng) &&
            isPointInBounds(lat, lng, bounds)
        ) {
            visibleIds.push(item.entity_id);
        }
    });

    return visibleIds;
}

/**
 * Custom hook that tracks which map markers are visible in the current viewport.
 * Listens to map `moveend` events and returns an array of visible item IDs.
 *
 * @param mapInstance - The Mapbox map instance
 * @param data - Array of search result items with lat/lng coordinates
 * @param debounceMs - Debounce delay in milliseconds (default: 150)
 * @returns Object containing visibleItemIds array
 *
 * @example
 * const { visibleItemIds } = useVisibleMapItems({
 *   mapInstance,
 *   data: validatedData,
 * });
 */
export function useVisibleMapItems({
    mapInstance,
    data,
    debounceMs = 150,
}: UseVisibleMapItemsParams): UseVisibleMapItemsResult {
    const [visibleItemIds, setVisibleItemIds] = React.useState<string[] | null>(null);
    const prevMapInstanceRef = React.useRef<Map | null>(null);
    const prevDataRef = React.useRef(data);

    // Expose null on the same render that data changes so the parent never dispatches
    // stale visibleItemIds (avoids hidden-results flash during location/search change).
    const dataJustChanged = prevDataRef.current !== data;
    if (dataJustChanged) {
        prevDataRef.current = data;
    }
    const exposedVisibleItemIds = dataJustChanged ? null : visibleItemIds;

    const calculateVisibleItems = React.useCallback(() => {
        if (!mapInstance || !data?.length) {
            setVisibleItemIds(null);
            return;
        }

        // Check if map has been removed (prevents errors during cleanup/HMR)
        // @ts-ignore - _removed is an internal property but necessary for safety
        if (mapInstance._removed) {
            return;
        }

        // Use accurate viewport bounds calculated from pixel corners
        // instead of getBounds() which can be inaccurate at high zoom levels
        // due to Web Mercator projection distortion
        const bounds = getAccurateViewportBounds(mapInstance);

        if (!bounds) {
            return;
        }

        const newVisibleIds = getVisibleItems(data, bounds);
        setVisibleItemIds(newVisibleIds);
    }, [mapInstance, data]);

    const debouncedCalculateVisibleItems = React.useMemo(
        () => debounce(calculateVisibleItems, debounceMs),
        [calculateVisibleItems, debounceMs]
    );

    // Calculate visible items on map move events.
    // Only run initial calculation when mapInstance changes (e.g. first load), not when data changes,
    // so that new search results show in the list immediately until moveend (flyTo complete).
    React.useEffect(() => {
        if (!mapInstance) return;

        // Check if map has been removed (prevents errors during cleanup/HMR)
        // @ts-ignore - _removed is an internal property but necessary for safety
        if (mapInstance._removed) return;

        const mapInstanceChanged = prevMapInstanceRef.current !== mapInstance;
        prevMapInstanceRef.current = mapInstance;

        if (mapInstanceChanged) {
            calculateVisibleItems();
        }

        // Listen to moveend event for pan/zoom changes
        const handleMoveEnd = () => {
            debouncedCalculateVisibleItems();
        };

        mapInstance.on('moveend', handleMoveEnd);

        return () => {
            debouncedCalculateVisibleItems.cancel();
            mapInstance.off('moveend', handleMoveEnd);
        };
    }, [mapInstance, calculateVisibleItems, debouncedCalculateVisibleItems]);

    // When data changes (e.g. new search), reset to null so the list shows all results
    // until the map has settled. Visibility is recalculated on moveend when flyTo completes.
    React.useEffect(() => {
        setVisibleItemIds(null);
    }, [data]);

    return React.useMemo(
        () => ({
            visibleItemIds: exposedVisibleItemIds,
        }),
        [exposedVisibleItemIds]
    );
}

// cSpell:ignore moveend unproject
